import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './common/Button';
import { Feedback } from './Feedback';
import { getFirestoreDb } from '@/services/firebase';
import { createLogger } from '@/services/loggingService';

const logger = createLogger('SiteFooter');

interface SiteFooterProps {
  onOpenPrivacy: () => void;
  onOpenTerms: () => void;
  onOpenCookies: () => void;
}

const sectionLinks = [
  { label: 'Features', hash: 'product' },
  { label: 'How it works', hash: 'how-it-works' },
  { label: 'Pricing', hash: 'pricing' },
];

const resourceLinks = [
  { label: 'Contact', href: 'mailto:jackatlasov1@gmail.com', external: true },
];

const knowledgeLinks = [
  { label: 'Methodology', path: '/methodology' },
  { label: 'F.A.Q.', path: '/faq' },
  { label: 'SKU Inventory', path: '/inventory/overview' },
];

export const SiteFooter: React.FC<SiteFooterProps> = ({ onOpenPrivacy, onOpenTerms, onOpenCookies }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavigateToSection = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, hash: string) => {
      event.preventDefault();
      if (location.pathname === '/') {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        navigate(`/#${hash}`);
      }
    },
    [location.pathname, navigate]
  );

  const handleNavigateToRoute = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, path: string) => {
      event.preventDefault();
      navigate(path);
    },
    [navigate]
  );

  const [subscribeStatus, setSubscribeStatus] = React.useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [subscribeMessage, setSubscribeMessage] = React.useState('');

  const handleSubscribe = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const form = event.currentTarget;
      const emailInput = form.querySelector<HTMLInputElement>('input[type="email"]');
      const email = emailInput?.value.trim().toLowerCase();

      if (!email) {
        setSubscribeStatus('error');
        setSubscribeMessage('Please enter a valid email address.');
        return;
      }

      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(email)) {
        setSubscribeStatus('error');
        setSubscribeMessage('That doesn’t look like a valid email address.');
        return;
      }

      try {
        setSubscribeStatus('loading');
        setSubscribeMessage('');
        const [firestore, dbInstance] = await Promise.all([
          import('firebase/firestore'),
          getFirestoreDb(),
        ]);
        await firestore.addDoc(firestore.collection(dbInstance, 'newsletterSubscribers'), {
          email,
          createdAt: firestore.serverTimestamp(),
        });
        if (emailInput) {
          emailInput.value = '';
        }
        setSubscribeStatus('success');
        setSubscribeMessage('Thanks! You’re on the list.');
      } catch (error) {
        logger.error('Failed to store subscriber email', { error: error instanceof Error ? error.message : String(error) });
        setSubscribeStatus('error');
        setSubscribeMessage('Something went wrong. Please try again.');
      }
    },
    []
  );

  return (
    <footer className="bg-slate-950 text-slate-300">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col md:flex-row gap-8 md:gap-6">
        <div className="space-y-3 md:flex-1">
          <h3 className="text-lg font-semibold text-white">Amazon Profit Studio</h3>
          <p className="text-sm text-slate-400 leading-relaxed">
            Your complete Amazon profit command center. Optimize ads, manage inventory, analyze margins, and verify results.
          </p>
        </div>
        <div className="flex flex-col md:flex-1">
          <h4 className="text-sm font-semibold text-white uppercase tracking-wide mb-3">Product</h4>
          <ul className="space-y-2 text-sm">
            {sectionLinks.map(link => (
              <li key={link.hash}>
                <a
                  href={`/#${link.hash}`}
                  onClick={event => handleNavigateToSection(event, link.hash)}
                  className="hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
            <li>
              <Feedback />
            </li>
          </ul>
        </div>
        <div className="flex flex-col md:flex-1">
          <h4 className="text-sm font-semibold text-white uppercase tracking-wide mb-3">Resources</h4>
          <ul className="space-y-2 text-sm">
            {knowledgeLinks.map(link => (
              <li key={link.path}>
                <a
                  href={link.path}
                  onClick={event => handleNavigateToRoute(event, link.path)}
                  className="hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
            {resourceLinks.map(link => (
              <li key={link.label}>
                <a
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noreferrer' : undefined}
                  className="hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col md:flex-1">
          <h4 className="text-sm font-semibold text-white uppercase tracking-wide mb-3">Stay in touch</h4>
          <p className="text-sm text-slate-400 mb-4">Receive product updates and new playbooks when they launch.</p>
          <form className="flex flex-col sm:flex-row gap-3" onSubmit={handleSubscribe}>
            <input
              type="email"
              placeholder="Work email"
              className="flex-1 rounded-md border border-slate-700 bg-slate-900/60 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Button type="submit" size="sm" disabled={subscribeStatus === 'loading'}>
              Subscribe
            </Button>
          </form>
          {subscribeMessage && (
            <p
              className={`mt-3 text-sm ${
                subscribeStatus === 'success' ? 'text-emerald-400' : 'text-amber-300'
              }`}
            >
              {subscribeMessage}
            </p>
          )}
        </div>
      </div>
      <div className="border-t border-slate-800 py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-4 text-xs text-slate-400 sm:flex-row sm:items-center sm:justify-between">
          <span>&copy; {new Date().getFullYear()} Amazon Profit Studio. All rights reserved.</span>
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            <button type="button" onClick={onOpenPrivacy} className="text-blue-400 hover:text-white transition-colors">
              Privacy
            </button>
            <span aria-hidden="true" className="hidden sm:inline text-slate-600">
              •
            </span>
            <button type="button" onClick={onOpenTerms} className="text-blue-400 hover:text-white transition-colors">
              Terms
            </button>
            <span aria-hidden="true" className="hidden sm:inline text-slate-600">
              •
            </span>
            <button type="button" onClick={onOpenCookies} className="text-blue-400 hover:text-white transition-colors">
              Cookies
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
